import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import PageHeader from '../../common/PageHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setAlert } from '../../../redux/actions';
import { isLogin } from '../../utils/helper';
import { password as passwordRules } from '../../utils/rules';
import PAlert from '../PAlert';

const PasswordResetForm = ({ withTitle = false }) => {
    const dispatch = useDispatch();

    const alert = useSelector((state) => state.privaGlobal.alert);

    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            pwd: '',
            pwd_confirmation: '',
            email: '',
            token: '',
        },
        validationSchema: Yup.object({
            pwd: Yup.string()
                .matches(
                    passwordRules.regex,
                    'Password must be at least 8 characters long, should contain at-least 1 Uppercase, 1 Lowercase, 1 Numeric characters'
                )
                .min(8, 'Minimum 8 characters')
                .required('Password is required'),
            pwd_confirmation: Yup.string()
                .oneOf([Yup.ref('pwd')], 'Password does not match')
                .required('Confirm password required'),
        }),
        onSubmit: async (values, { setErrors }) => {
            setIsLoading(true);
            await axios
                .post('/api/reset-password', { ...values })
                .then((response) => {
                    dispatch(
                        setAlert({
                            show: true,
                            type: 'success',
                            body: 'Password successfully reset. Redirecting to login page',
                        })
                    );
                    setTimeout(() => {
                        localStorage.clear();
                        window.location = '/login';
                    }, 5000);
                })
                .catch((error) => {
                    console.log({ error });
                    dispatch(
                        setAlert({
                            show: true,
                            type: 'error',
                            body: 'Unable to reset password',
                        })
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
    });

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowNewPassword = () =>
        setShowNewPassword((newPassword) => !newPassword);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleCurrentPasswordChange = (e) => {
        formik.setFieldError('pwd', null);
        formik.setFieldValue('pwd', e.target.value, true);
    };

    const handleNewPasswordChange = (e) => {
        formik.setFieldError('pwd_confirmation', null);
        formik.setFieldValue('pwd_confirmation', e.target.value, true);
    };

    const handleShowAlert = () => {
        dispatch(
            setAlert({
                show: false,
                title: '',
                body: '',
                type: '',
            })
        );
    };

    useEffect(() => {
        const pageURL = window.location.href;
        const lastURLSegment = pageURL
            .substr(pageURL.lastIndexOf('/') + 1)
            .split('?');
        const urlParams = new URLSearchParams(window.location.search);

        formik.setFieldValue('token', lastURLSegment[0], false);
        formik.setFieldValue('email', urlParams.get('email'), false);
    }, []);

    return (
        <div className="password-reset-form">
            {withTitle ? (
                <PageHeader
                    closable={false}
                    backable={false}
                    title="Password Reset"
                />
            ) : null}
            <div
                css={css`
                    margin-top: 15px;
                `}
            >
                <form
                    onSubmit={formik.handleSubmit}
                    className="mt-4"
                >
                    <div
                        css={css`
                            display: flex;
                            gap: 15px;
                            @media (max-width: 899px) {
                                display: block;
                            }
                        `}
                    >
                        <div
                            css={css`
                                @media (max-width: 899px) {
                                    display: block;
                                    margin-bottom: 15px;
                                }
                            `}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                disabled={isLoading}
                                name="pwd"
                                id="pwd"
                                // onBlur={formik.handleBlur}
                                onChange={handleCurrentPasswordChange}
                                value={formik.values.pwd}
                                error={
                                    !!formik.errors.pwd && formik.touched.pwd
                                }
                                helperText={
                                    formik.errors.pwd && formik.touched.pwd
                                        ? formik.errors.pwd
                                        : ''
                                }
                                type={showPassword ? 'text' : 'password'}
                                label="Password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={
                                                    handleClickShowPassword
                                                }
                                                onMouseDown={
                                                    handleMouseDownPassword
                                                }
                                                edge="end"
                                            >
                                                {!showPassword ? (
                                                    <i className="fa-solid fa-eye-slash"></i>
                                                ) : (
                                                    <i className="fa-solid fa-eye"></i>
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: '10px',
                                }}
                            />
                        </div>
                        <div
                            css={css`
                                @media (max-width: 899px) {
                                    display: block;
                                    margin-bottom: 15px;
                                }
                            `}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                disabled={isLoading}
                                name="pwd_confirmation"
                                id="pwd_confirmation"
                                // onBlur={formik.handleBlur}
                                onChange={handleNewPasswordChange}
                                value={formik.values.pwd_confirmation}
                                error={
                                    !!formik.errors.pwd_confirmation &&
                                    formik.touched.pwd_confirmation
                                }
                                helperText={
                                    formik.errors.pwd_confirmation &&
                                    formik.touched.pwd_confirmation
                                        ? formik.errors.pwd_confirmation
                                        : ''
                                }
                                type={showNewPassword ? 'text' : 'password'}
                                label="Confirm Password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={
                                                    handleClickShowNewPassword
                                                }
                                                onMouseDown={
                                                    handleMouseDownPassword
                                                }
                                                edge="end"
                                            >
                                                {!showNewPassword ? (
                                                    <i className="fa-solid fa-eye-slash"></i>
                                                ) : (
                                                    <i className="fa-solid fa-eye"></i>
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: '10px',
                                }}
                            />
                        </div>
                    </div>

                    <div
                        css={css`
                            display: flex;
                            flex: 1;
                            justify-content: flex-end;
                            margin-top: 15px;
                        `}
                    >
                        <Button
                            type="submit"
                            disabled={isLoading}
                            sx={{
                                borderRadius: '10px',
                            }}
                            variant="contained"
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </div>

            <PAlert
                onClose={alert.onClose}
                show={alert.show}
                setShow={handleShowAlert}
                title={alert.title}
                body={alert.body}
                bodyStyle={alert.bodyStyle}
                footerStyle={alert.footerStyle}
                size={alert.size ?? 'sm'}
                modalStyle={{
                    zIndex: 21474836111,
                }}
            />
        </div>
    );
};

export default PasswordResetForm;
